/**
 * Asset Assessment Engine
 * Analyzes uploaded asset data and generates diagnostic prescriptions
 */

// Risk scoring weights
const RISK_WEIGHTS = {
  age: 0.25,
  load: 0.20,
  thermal: 0.25,
  oilCondition: 0.15,
  dgaStatus: 0.15
};

// Age risk scoring
const getAgeRisk = (age) => {
  if (age <= 5) return { score: 1, level: "low", label: "New" };
  if (age <= 15) return { score: 2, level: "low", label: "Mid-life" };
  if (age <= 25) return { score: 3, level: "medium", label: "Aging" };
  if (age <= 35) return { score: 4, level: "high", label: "End-of-life" };
  return { score: 5, level: "critical", label: "Beyond Design Life" };
};

// Load risk scoring
const getLoadRisk = (avgLoad, peakLoad) => {
  const effectiveLoad = Math.max(avgLoad, peakLoad * 0.8);
  if (effectiveLoad <= 40) return { score: 1, level: "low", label: "Light Loading" };
  if (effectiveLoad <= 60) return { score: 2, level: "low", label: "Normal Loading" };
  if (effectiveLoad <= 75) return { score: 3, level: "medium", label: "Moderate Loading" };
  if (effectiveLoad <= 90) return { score: 4, level: "high", label: "Heavy Loading" };
  return { score: 5, level: "critical", label: "Overloaded" };
};

// Thermal risk scoring
const getThermalRisk = (peakTemp, ambientTemp) => {
  const adjustedTemp = peakTemp || (ambientTemp ? ambientTemp + 40 : null);
  if (!adjustedTemp) return { score: 2, level: "unknown", label: "Unknown" };
  if (adjustedTemp <= 75) return { score: 1, level: "low", label: "Normal" };
  if (adjustedTemp <= 85) return { score: 2, level: "low", label: "Acceptable" };
  if (adjustedTemp <= 95) return { score: 3, level: "medium", label: "Elevated" };
  if (adjustedTemp <= 105) return { score: 4, level: "high", label: "High" };
  return { score: 5, level: "critical", label: "Critical" };
};

// Oil condition risk scoring
const getOilConditionRisk = (oilCondition) => {
  const conditions = {
    "excellent": { score: 1, level: "low", label: "Excellent" },
    "good": { score: 1, level: "low", label: "Good" },
    "fair": { score: 3, level: "medium", label: "Fair" },
    "poor": { score: 4, level: "high", label: "Poor" },
    "critical": { score: 5, level: "critical", label: "Critical" }
  };
  return conditions[oilCondition?.toLowerCase()] || { score: 2, level: "unknown", label: "Unknown" };
};

// DGA status risk scoring (simplified)
const getDGARisk = (dgaData) => {
  if (!dgaData || !dgaData.h2) return { score: 2, level: "unknown", label: "No DGA Data" };
  
  // Simplified DGA assessment based on key gases
  const h2 = parseFloat(dgaData.h2) || 0;
  const c2h2 = parseFloat(dgaData.c2h2) || 0;
  const c2h4 = parseFloat(dgaData.c2h4) || 0;
  const ch4 = parseFloat(dgaData.ch4) || 0;
  
  // Critical acetylene levels indicate arcing
  if (c2h2 > 35) return { score: 5, level: "critical", label: "Critical - Arcing Suspected" };
  if (c2h2 > 10) return { score: 4, level: "high", label: "Elevated Acetylene" };
  
  // High ethylene indicates thermal fault
  if (c2h4 > 150) return { score: 4, level: "high", label: "Thermal Fault Indicated" };
  
  // High hydrogen indicates partial discharge
  if (h2 > 500) return { score: 4, level: "high", label: "Elevated Hydrogen" };
  if (h2 > 200) return { score: 3, level: "medium", label: "Moderate Hydrogen" };
  
  // Combined total combustible gases
  const tcg = h2 + ch4 + c2h2 + c2h4 + (parseFloat(dgaData.c2h6) || 0);
  if (tcg > 1000) return { score: 4, level: "high", label: "High TCG" };
  if (tcg > 500) return { score: 3, level: "medium", label: "Moderate TCG" };
  
  return { score: 1, level: "low", label: "Normal" };
};

// Calculate overall risk score
const calculateOverallRisk = (riskFactors) => {
  let totalScore = 0;
  let totalWeight = 0;
  
  Object.entries(RISK_WEIGHTS).forEach(([key, weight]) => {
    if (riskFactors[key] && riskFactors[key].score) {
      totalScore += riskFactors[key].score * weight;
      totalWeight += weight;
    }
  });
  
  const normalizedScore = totalWeight > 0 ? totalScore / totalWeight : 2.5;
  
  if (normalizedScore <= 1.5) return { level: "low", color: "#22c55e", label: "LOW RISK" };
  if (normalizedScore <= 2.5) return { level: "medium", color: "#eab308", label: "MEDIUM RISK" };
  if (normalizedScore <= 3.5) return { level: "medium-high", color: "#f97316", label: "MEDIUM-HIGH RISK" };
  if (normalizedScore <= 4.5) return { level: "high", color: "#ef4444", label: "HIGH RISK" };
  return { level: "critical", color: "#dc2626", label: "CRITICAL" };
};

// Generate prescribed tests based on risk factors
const generatePrescribedTests = (riskFactors, assetData) => {
  const priority1 = [];
  const priority2 = [];
  
  // Age-based tests
  if (riskFactors.age.score >= 3) {
    priority1.push({
      name: "Furan Analysis",
      reason: `Asset is ${assetData.age} years old - paper degradation assessment critical`,
      component: "Main Tank & Oil"
    });
    priority1.push({
      name: "SFRA (Sweep Frequency Response)",
      reason: "Detect winding deformation accumulated over years of service",
      component: "Winding & Magnetic Circuit"
    });
  }
  
  // Always recommend DGA for any assessment
  priority1.push({
    name: "DGA (Dissolved Gas Analysis)",
    reason: riskFactors.dga?.level === "unknown" 
      ? "No recent DGA data - essential for baseline assessment"
      : "Continue DGA trending for fault detection",
    component: "Main Tank & Oil"
  });
  
  // Load-based tests
  if (riskFactors.load.score >= 3) {
    priority1.push({
      name: "Infrared Thermography",
      reason: `Operating at ${assetData.avgLoad}% average load - thermal survey recommended`,
      component: "Winding & Magnetic Circuit"
    });
    priority2.push({
      name: "Cooling System Inspection",
      reason: "High loading requires verification of cooling capacity",
      component: "Cooling System"
    });
  }
  
  // Thermal-based tests
  if (riskFactors.thermal.score >= 3) {
    priority1.push({
      name: "Oil Quality Test",
      reason: "Elevated temperatures accelerate oil degradation",
      component: "Main Tank & Oil"
    });
    if (riskFactors.thermal.score >= 4) {
      priority1.push({
        name: "Degree of Polymerization",
        reason: `Peak temperature ${assetData.peakTemp}°C - direct paper assessment needed`,
        component: "Main Tank & Oil"
      });
    }
  }
  
  // Oil condition tests
  if (riskFactors.oilCondition.score >= 3) {
    priority1.push({
      name: "Oil Dielectric Strength",
      reason: `Oil condition rated as ${riskFactors.oilCondition.label}`,
      component: "Main Tank & Oil"
    });
    priority2.push({
      name: "Oil Moisture Content",
      reason: "Verify moisture levels in degraded oil",
      component: "Main Tank & Oil"
    });
  }
  
  // DGA-based tests
  if (riskFactors.dga.score >= 4) {
    priority1.push({
      name: "Power Factor Test",
      reason: `DGA indicates ${riskFactors.dga.label} - insulation assessment needed`,
      component: "Winding & Magnetic Circuit"
    });
  }
  
  // Standard tests for any assessment
  if (priority1.length < 3) {
    priority2.push({
      name: "Insulation Resistance",
      reason: "Standard insulation health check",
      component: "Winding & Magnetic Circuit"
    });
  }
  
  if (riskFactors.age.score >= 2) {
    priority2.push({
      name: "Bushing Power Factor",
      reason: "Bushings are critical failure points - verify condition",
      component: "HV/LV/MV Bushings"
    });
  }
  
  // Remove duplicates
  const uniquePriority1 = priority1.filter((test, index, self) =>
    index === self.findIndex(t => t.name === test.name)
  );
  const uniquePriority2 = priority2.filter((test, index, self) =>
    index === self.findIndex(t => t.name === test.name) && 
    !uniquePriority1.some(p1 => p1.name === test.name)
  );
  
  return { priority_1: uniquePriority1, priority_2: uniquePriority2 };
};

// Generate prescribed monitoring based on risk factors
const generatePrescribedMonitoring = (riskFactors, assetData) => {
  const critical = [];
  const recommended = [];
  
  // High risk assets need continuous monitoring
  if (riskFactors.age.score >= 4 || riskFactors.dga.score >= 4) {
    critical.push({
      name: "Online DGA Monitor",
      reason: "Continuous fault gas trending essential for high-risk asset",
      component: "Main Tank & Oil"
    });
  }
  
  // Thermal concerns
  if (riskFactors.thermal.score >= 3 || riskFactors.load.score >= 4) {
    critical.push({
      name: "Winding Hot Spot Monitor",
      reason: `Thermal stress detected - continuous temperature monitoring needed`,
      component: "Winding & Magnetic Circuit"
    });
  }
  
  // Aging transformers need bushing monitoring
  if (riskFactors.age.score >= 3) {
    critical.push({
      name: "Bushing Monitors",
      reason: "Bushings are common failure mode in aged transformers",
      component: "HV/LV/MV Bushings"
    });
  }
  
  // Load monitoring for heavily loaded units
  if (riskFactors.load.score >= 3) {
    recommended.push({
      name: "Load Profile Monitoring",
      reason: "Track loading patterns for thermal aging assessment",
      component: "Winding & Magnetic Circuit"
    });
    recommended.push({
      name: "Cooling System Monitoring",
      reason: "Ensure cooling keeps pace with loading",
      component: "Cooling System"
    });
  }
  
  // Standard recommendations
  if (riskFactors.thermal.score >= 2) {
    recommended.push({
      name: "Temperature Sensors",
      reason: "Track top oil and ambient temperature trends",
      component: "Main Tank & Oil"
    });
  }
  
  if (riskFactors.dga.score >= 3 && !critical.some(m => m.name.includes("DGA"))) {
    recommended.push({
      name: "Enhanced DGA Monitoring",
      reason: "More frequent DGA sampling recommended",
      component: "Main Tank & Oil"
    });
  }
  
  // Remove duplicates
  const uniqueCritical = critical.filter((m, index, self) =>
    index === self.findIndex(t => t.name === m.name)
  );
  const uniqueRecommended = recommended.filter((m, index, self) =>
    index === self.findIndex(t => t.name === m.name) &&
    !uniqueCritical.some(c => c.name === m.name)
  );
  
  return { critical: uniqueCritical, recommended: uniqueRecommended };
};

// Generate expert recommendation text
const generateRecommendation = (riskFactors, overallRisk, assetData) => {
  const recommendations = [];
  
  if (overallRisk.level === "critical") {
    recommendations.push("URGENT: This asset requires immediate comprehensive assessment.");
  } else if (overallRisk.level === "high") {
    recommendations.push("This asset presents elevated risk and requires priority attention.");
  }
  
  if (riskFactors.age.score >= 4) {
    recommendations.push(`At ${assetData.age} years, strategic replacement planning should begin. Furan analysis will help estimate remaining useful life.`);
  }
  
  if (riskFactors.thermal.score >= 4) {
    recommendations.push(`Peak temperatures of ${assetData.peakTemp}°C accelerate aging exponentially. Consider load management or cooling upgrades.`);
  }
  
  if (riskFactors.load.score >= 4) {
    recommendations.push(`Operating at ${assetData.avgLoad}% average load may limit future capacity. Monitor thermal performance closely.`);
  }
  
  if (riskFactors.dga.score >= 4) {
    recommendations.push(`DGA results indicate ${riskFactors.dga.label}. Increased monitoring frequency and follow-up testing recommended.`);
  }
  
  if (recommendations.length === 0) {
    recommendations.push("Continue routine maintenance and monitoring program. Schedule periodic testing as per standard intervals.");
  }
  
  return recommendations.join(" ");
};

// Main assessment function
export const assessAsset = (assetData) => {
  // Calculate individual risk factors
  const riskFactors = {
    age: getAgeRisk(assetData.age || 0),
    load: getLoadRisk(assetData.avgLoad || 0, assetData.peakLoad || 0),
    thermal: getThermalRisk(assetData.peakTemp, assetData.ambientTemp),
    oilCondition: getOilConditionRisk(assetData.oilCondition),
    dga: getDGARisk(assetData.dga)
  };
  
  // Calculate overall risk
  const overallRisk = calculateOverallRisk(riskFactors);
  
  // Generate prescriptions
  const prescribedTests = generatePrescribedTests(riskFactors, assetData);
  const prescribedMonitoring = generatePrescribedMonitoring(riskFactors, assetData);
  const recommendation = generateRecommendation(riskFactors, overallRisk, assetData);
  
  // Build the scenario object
  return {
    id: `custom_${Date.now()}`,
    name: assetData.assetName || "Custom Asset Assessment",
    shortDescription: `${assetData.ratedPower || "N/A"} MVA, ${assetData.age || "N/A"} years old`,
    icon: overallRisk.level === "critical" ? "alert-triangle" : 
          overallRisk.level === "high" ? "thermometer" :
          riskFactors.age.score >= 3 ? "clock" : "zap",
    risk_level: overallRisk.level,
    risk_color: overallRisk.color,
    isCustom: true,
    
    situation: {
      summary: `Assessment for ${assetData.assetName || "transformer"} based on provided operational data. ${
        assetData.location ? `Located at ${assetData.location}.` : ""
      } This ${assetData.age || "N/A"}-year-old ${assetData.ratedPower || "N/A"} MVA transformer is operating at ${assetData.avgLoad || "N/A"}% average load.`,
      bullet_points: [
        `Age: ${assetData.age || "N/A"} years (${riskFactors.age.label})`,
        `Loading: ${assetData.avgLoad || "N/A"}% average, ${assetData.peakLoad || "N/A"}% peak (${riskFactors.load.label})`,
        `Thermal Status: ${riskFactors.thermal.label}${assetData.peakTemp ? ` - ${assetData.peakTemp}°C peak` : ""}`,
        `Oil Condition: ${riskFactors.oilCondition.label}`,
        `DGA Status: ${riskFactors.dga.label}`
      ]
    },
    
    sample_specs: {
      rated_power_mva: assetData.ratedPower || "N/A",
      voltage_hv_kv: assetData.voltageHV || "N/A",
      voltage_lv_kv: assetData.voltageLV || "N/A",
      year_manufactured: assetData.yearManufactured || (new Date().getFullYear() - (assetData.age || 0)),
      cooling_type: assetData.coolingType || "N/A",
      age_years: assetData.age || "N/A"
    },
    
    operating_conditions: {
      avg_load_percent: assetData.avgLoad || 0,
      peak_load_percent: assetData.peakLoad || 0,
      ambient_temp_c: assetData.ambientTemp || "N/A",
      peak_temp_c: assetData.peakTemp || "N/A",
      status: `In Service - ${overallRisk.label}`
    },
    
    key_concerns: [
      riskFactors.age.score >= 3 ? `Aging asset (${riskFactors.age.label})` : null,
      riskFactors.load.score >= 3 ? `${riskFactors.load.label} condition` : null,
      riskFactors.thermal.score >= 3 ? `Thermal stress (${riskFactors.thermal.label})` : null,
      riskFactors.oilCondition.score >= 3 ? `Oil degradation (${riskFactors.oilCondition.label})` : null,
      riskFactors.dga.score >= 3 ? `DGA concern (${riskFactors.dga.label})` : null
    ].filter(Boolean),
    
    risk_factors: riskFactors,
    
    prescribed_tests: prescribedTests,
    prescribed_monitoring: prescribedMonitoring,
    recommendation_summary: recommendation
  };
};

// Parse CSV data to asset object
export const parseCSVToAsset = (csvText) => {
  const lines = csvText.trim().split('\n');
  const asset = {};
  
  lines.forEach(line => {
    const [key, value] = line.split(',').map(s => s.trim());
    if (key && value && value !== '' && value.toLowerCase() !== 'n/a') {
      // Map CSV fields to asset properties
      const fieldMap = {
        'asset_name': 'assetName',
        'asset_id': 'assetId',
        'location': 'location',
        'rated_power_mva': 'ratedPower',
        'voltage_hv_kv': 'voltageHV',
        'voltage_lv_kv': 'voltageLV',
        'year_manufactured': 'yearManufactured',
        'cooling_type': 'coolingType',
        'age_years': 'age',
        'avg_load_percent': 'avgLoad',
        'peak_load_percent': 'peakLoad',
        'ambient_temp_c': 'ambientTemp',
        'peak_winding_temp_c': 'peakTemp',
        'oil_condition': 'oilCondition',
        'dga_h2_ppm': 'dga.h2',
        'dga_ch4_ppm': 'dga.ch4',
        'dga_c2h2_ppm': 'dga.c2h2',
        'dga_c2h4_ppm': 'dga.c2h4',
        'dga_c2h6_ppm': 'dga.c2h6',
        'dga_co_ppm': 'dga.co',
        'dga_co2_ppm': 'dga.co2'
      };
      
      const mappedKey = fieldMap[key.toLowerCase()];
      if (mappedKey) {
        if (mappedKey.startsWith('dga.')) {
          if (!asset.dga) asset.dga = {};
          asset.dga[mappedKey.split('.')[1]] = parseFloat(value);
        } else {
          asset[mappedKey] = isNaN(parseFloat(value)) ? value : parseFloat(value);
        }
      }
    }
  });
  
  return asset;
};

// Generate CSV template
export const generateCSVTemplate = () => {
  return `Field,Value
asset_name,Enter Asset Name
asset_id,Enter Asset ID (optional)
location,Enter Location (optional)
rated_power_mva,Enter MVA Rating
voltage_hv_kv,Enter HV Voltage (kV)
voltage_lv_kv,Enter LV Voltage (kV)
year_manufactured,Enter Year (e.g. 2005)
cooling_type,Enter Cooling Type (e.g. ONAN/ONAF)
age_years,Enter Age in Years
avg_load_percent,Enter Average Load %
peak_load_percent,Enter Peak Load %
ambient_temp_c,Enter Ambient Temperature °C (optional)
peak_winding_temp_c,Enter Peak Winding Temperature °C (optional)
oil_condition,Enter: Excellent/Good/Fair/Poor (optional)
dga_h2_ppm,Enter H2 ppm (optional)
dga_ch4_ppm,Enter CH4 ppm (optional)
dga_c2h2_ppm,Enter C2H2 ppm (optional)
dga_c2h4_ppm,Enter C2H4 ppm (optional)
dga_c2h6_ppm,Enter C2H6 ppm (optional)
dga_co_ppm,Enter CO ppm (optional)
dga_co2_ppm,Enter CO2 ppm (optional)`;
};
